// Billing history bar chart (Chart.js via react-chartjs-2). Stacked by category
// (Workers/R2/D1/KV/DO + base plan). The current month (is_final === 0) is the
// projected estimate — we annotate it in the tooltip and dim it slightly.
import { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  type ChartData,
  type ChartOptions,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import type { BillingMonthRow, CostCategory } from "../lib/api";
import { useThemeStore } from "../lib/theme";
import { monthLabel, usd, pct } from "../lib/format";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const CATEGORY_FIELDS: { key: CostCategory | "Base"; field: keyof BillingMonthRow; color: string }[] = [
  { key: "Base", field: "base_usd", color: "#94a3b8" }, // slate-400
  { key: "Workers", field: "workers_usd", color: "#3b82f6" }, // blue-500
  { key: "R2", field: "r2_usd", color: "#f59e0b" }, // amber-500
  { key: "D1", field: "d1_usd", color: "#22c55e" }, // green-500
  { key: "KV", field: "kv_usd", color: "#a855f7" }, // purple-500
  { key: "DO", field: "do_usd", color: "#ec4899" }, // pink-500
];

/** Sum a history row (base + per-category). Treats null categories as 0. */
function rowTotal(r: BillingMonthRow): number {
  if (typeof r.total_usd === "number" && Number.isFinite(r.total_usd)) return r.total_usd;
  return (
    Number(r.base_usd ?? 0) +
    Number(r.workers_usd ?? 0) +
    Number(r.r2_usd ?? 0) +
    Number(r.d1_usd ?? 0) +
    Number(r.kv_usd ?? 0) +
    Number(r.do_usd ?? 0)
  );
}

export function BillingHistoryChart({
  rows,
  capUsd,
}: {
  rows: BillingMonthRow[];
  capUsd?: number;
}) {
  const theme = useThemeStore((s) => s.theme);
  const dark = theme === "dark";

  // billing_month comes DESC; chart reads left→right oldest→newest.
  const ordered = useMemo(() => [...rows].sort((a, b) => a.month.localeCompare(b.month)), [rows]);

  const data: ChartData<"bar"> = useMemo(() => {
    const labels = ordered.map((r) =>
      r.is_final === 0 ? `${monthLabel(r.month)} (est.)` : monthLabel(r.month),
    );
    const datasets = CATEGORY_FIELDS.map((c) => ({
      label: c.key,
      data: ordered.map((r) => Number(r[c.field] ?? 0)),
      backgroundColor: ordered.map((r) => (r.is_final === 0 ? c.color + "99" : c.color)),
      borderColor: ordered.map((r) => (r.is_final === 0 ? c.color : "transparent")),
      borderWidth: ordered.map((r) => (r.is_final === 0 ? 2 : 0)),
      stack: "bill",
    }));
    return { labels, datasets };
  }, [ordered]);

  const gridColor = dark ? "rgba(148,163,184,0.18)" : "rgba(100,116,139,0.15)";
  const tickColor = dark ? "#cbd5e1" : "#475569";

  const options: ChartOptions<"bar"> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: "index", intersect: false },
      scales: {
        x: { stacked: true, grid: { display: false }, ticks: { color: tickColor } },
        y: {
          stacked: true,
          beginAtZero: true,
          grid: { color: gridColor },
          ticks: {
            color: tickColor,
            callback: (v) => usd(Number(v)),
          },
        },
      },
      plugins: {
        legend: { labels: { color: tickColor, boxWidth: 12 } },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${usd(Number(ctx.parsed.y))}`,
            footer: (items) => {
              const total = items.reduce((s, i) => s + Number(i.parsed.y), 0);
              return `Total: ${usd(total)}`;
            },
          },
        },
      },
    }),
    [gridColor, tickColor],
  );

  // Table: newest first. MoM delta = total − previous month total. vs budget = total/cap.
  const tableRows = useMemo(() => {
    const desc = [...ordered].reverse();
    return desc.map((r, i) => {
      const total = rowTotal(r);
      const prev = desc[i + 1] ? rowTotal(desc[i + 1]) : total;
      return { row: r, total, mom: total - prev };
    });
  }, [ordered]);

  const cap = typeof capUsd === "number" && capUsd > 0 ? capUsd : null;

  return (
    <div>
      <div className="h-64 w-full">
        <Bar data={data} options={options} />
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-right text-xs tabular-nums">
          <thead>
            <tr className="border-b border-gray-300 text-gray-500 dark:border-gray-600 dark:text-gray-400">
              <th className="px-2 py-2 text-left font-semibold">Month</th>
              <th className="px-2 py-2 font-semibold">Workers</th>
              <th className="px-2 py-2 font-semibold">R2</th>
              <th className="px-2 py-2 font-semibold">D1</th>
              <th className="px-2 py-2 font-semibold">KV</th>
              <th className="px-2 py-2 font-semibold">Total</th>
              <th className="px-2 py-2 font-semibold">vs budget</th>
              <th className="px-2 py-2 font-semibold">MoM</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map(({ row, total, mom }) => {
              const vb = cap ? total / cap : null;
              const vbClass =
                vb == null
                  ? "text-gray-400 dark:text-gray-500"
                  : vb >= 1
                    ? "text-red-600 dark:text-red-400"
                    : vb >= 0.8
                      ? "text-amber-600 dark:text-amber-400"
                      : "text-green-600 dark:text-green-400";
              const momClass =
                mom > 0
                  ? "text-red-600 dark:text-red-400"
                  : mom < 0
                    ? "text-green-600 dark:text-green-400"
                    : "text-gray-400 dark:text-gray-500";
              // Workers column shows base + workers (matches mockup's "Workers" = base+w).
              const workersCol = Number(row.base_usd ?? 0) + Number(row.workers_usd ?? 0);
              return (
                <tr
                  key={row.month}
                  className="border-b border-gray-100 last:border-0 dark:border-gray-700/60"
                >
                  <td className="px-2 py-2 text-left">
                    {monthLabel(row.month)}
                    {row.is_final === 0 && (
                      <span className="ml-1.5 rounded bg-gray-100 px-1.5 py-px text-[10px] font-semibold text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                        projected
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2">{usd(workersCol)}</td>
                  <td className="px-2 py-2">{usd(row.r2_usd)}</td>
                  <td className="px-2 py-2">{usd(row.d1_usd)}</td>
                  <td className="px-2 py-2">{usd(row.kv_usd)}</td>
                  <td className="px-2 py-2 font-semibold">{usd(total)}</td>
                  <td className={"px-2 py-2 " + vbClass}>{vb == null ? "—" : pct(vb)}</td>
                  <td className={"px-2 py-2 " + momClass}>
                    {mom > 0 ? "+" : ""}
                    {usd(mom)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {cap == null && (
          <p className="mt-2 text-[11px] text-gray-400 dark:text-gray-500">
            limited — vs-budget needs budget cap
          </p>
        )}
      </div>
    </div>
  );
}
